/**
 * PortfolioIntelligenceTab — Reusable Portfolio Intelligence display component
 * Used by both SMEEvolutionView and CorporateEvolutionView.
 *
 * Shows: Trajectory | Credibility | Risk | Target Performance | Portfolio Decision
 * + Timeline table + Key Insights
 */
import React, { useState, useEffect } from "react";
import { TrendingDown, TrendingUp, Minus, Shield, AlertTriangle, Target, CheckCircle2, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

// ── Color mappings ───────────────────────────────────────────────────────────
const COLORS = {
  emerald: { card: "border-emerald-200 bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", icon: "text-emerald-500" },
  teal:    { card: "border-teal-200 bg-teal-50",       badge: "bg-teal-100 text-teal-700",       icon: "text-teal-500"    },
  amber:   { card: "border-amber-200 bg-amber-50",     badge: "bg-amber-100 text-amber-700",     icon: "text-amber-500"   },
  red:     { card: "border-red-200 bg-red-50",         badge: "bg-red-100 text-red-700",         icon: "text-red-500"     },
  slate:   { card: "border-slate-200 bg-slate-50",     badge: "bg-slate-100 text-slate-600",     icon: "text-slate-400"   },
};

function ScoreCard({ title, label, color, score, description, subtitle }) {
  const c = COLORS[color] || COLORS.slate;
  return (
    <div className={`rounded-xl border p-5 ${c.card}`} data-testid={`score-card-${title.toLowerCase().replace(/\s/g,"-")}`}>
      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">{title}</p>
      <p className={`text-sm font-bold mb-1 ${c.icon}`}>{label}</p>
      {score !== undefined && (
        <div className="flex items-center gap-2 mb-2">
          <div className="h-1.5 flex-1 bg-white/60 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${color === "emerald" ? "bg-emerald-400" : color === "teal" ? "bg-teal-400" : color === "amber" ? "bg-amber-400" : "bg-red-400"}`}
              style={{ width: `${Math.min(score, 100)}%` }} />
          </div>
          <span className="text-[9px] font-bold text-slate-500">{score}/100</span>
        </div>
      )}
      <p className="text-[8px] text-slate-500 leading-relaxed">{description}</p>
      {subtitle && <p className="text-[7px] text-slate-400 mt-1 italic">{subtitle}</p>}
    </div>
  );
}

function InsightRow({ insight }) {
  const iconMap = {
    trending_down: <TrendingDown size={13} className="text-emerald-500 shrink-0" />,
    trending_up:   <TrendingUp   size={13} className="text-red-500 shrink-0"    />,
    minus:         <Minus        size={13} className="text-amber-500 shrink-0"  />,
    shield:        <Shield       size={13} className="text-amber-500 shrink-0"  />,
    shield_check:  <Shield       size={13} className="text-emerald-500 shrink-0"/>,
    alert_triangle:<AlertTriangle size={13} className="text-red-500 shrink-0"   />,
    target:        <Target       size={13} className="text-teal-500 shrink-0"   />,
  };
  const typeBg = insight.type === "positive" ? "bg-emerald-50 border-emerald-100" :
                 insight.type === "critical"  ? "bg-red-50 border-red-100"        :
                 "bg-amber-50 border-amber-100";
  return (
    <div className={`flex items-start gap-2.5 px-4 py-2.5 rounded-xl border ${typeBg} mb-2`}>
      {iconMap[insight.icon] || <CheckCircle2 size={13} className="shrink-0" />}
      <p className="text-[8px] text-slate-600 leading-relaxed">{insight.text}</p>
    </div>
  );
}

export default function PortfolioIntelligenceTab({ workspace, mode = "operational" }) {
  const [data, setData]           = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [showNarrative, setShowNarrative] = useState(false);

  useEffect(() => {
    setLoading(true);
    const endpoint = workspace === "sme"
      ? `/sme/periodic/portfolio?mode=${mode}`
      : `/corporate/periodic/portfolio?mode=${mode}`;
    const narrativeEndpoint = workspace === "sme"
      ? `/sme/periodic/investment-narrative?mode=${mode}`
      : `/corporate/periodic/investment-narrative?mode=${mode}`;
    Promise.all([
      client.get(endpoint),
      client.get(narrativeEndpoint).catch(() => null),
    ]).then(([portRes, narRes]) => {
      setData(portRes.data);
      setNarrative(narRes?.data || null);
      setLoading(false);
    }).catch(e => { setError(e); setLoading(false); });
  }, [workspace, mode]);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-sm font-bold text-red-700">Portfolio assessment unavailable</p>
      <p className="text-xs text-red-500 mt-1">Requires at least 3 calculated periods.</p>
    </div>
  );

  if (!data) return null;

  const { trajectory, credibility, risk, target_performance: tp,
          portfolio_decision: pd, timeline, key_insights,
          periods_assessed, assessment_scope } = data;

  const PORT_COLOR = COLORS[pd?.color] || COLORS.slate;
  const insufficient = trajectory?.trajectory_key === "insufficient_data";

  return (
    <div className="space-y-5" data-testid="portfolio-intelligence">
      {/* Header */}
      <div className="bg-[#0F172A] rounded-xl p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              Portfolio Intelligence — {mode.toUpperCase()} MODE
            </p>
            <p className="text-lg font-bold text-white">{pd?.outcome_label || "—"}</p>
            <p className="text-[8px] text-slate-400 mt-1">{assessment_scope} · {periods_assessed} periods assessed</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-teal-400">{pd?.portfolio_score}</p>
            <p className="text-[8px] text-slate-400">Portfolio Score</p>
          </div>
        </div>
        <p className="text-[8px] text-slate-300 mt-3 leading-relaxed">{pd?.description}</p>
        {pd?.conditions?.length > 0 && (
          <div className="mt-3 space-y-1">
            {pd.conditions.map((c, i) => (
              <p key={i} className="text-[8px] text-amber-400">→ {c}</p>
            ))}
          </div>
        )}
      </div>

      {insufficient && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <p className="text-sm font-bold text-amber-700">Insufficient data for full trajectory analysis</p>
          <p className="text-xs text-amber-600 mt-1">3 periods minimum required. Currently: {periods_assessed}.</p>
        </div>
      )}

      {/* 4 Score Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ScoreCard
          title="Trajectory"
          label={trajectory?.label}
          color={trajectory?.color}
          score={trajectory?.score}
          description={trajectory?.description}
          subtitle={trajectory?.direction && trajectory?.stability ? `${trajectory.direction} · ${trajectory.stability}` : undefined}
        />
        <ScoreCard
          title="Data Credibility"
          label={credibility?.label}
          color={credibility?.color}
          score={credibility?.score}
          description={credibility?.description}
          subtitle={`Avg confidence: ${credibility?.avg_confidence?.toFixed(0) ?? "—"}/100`}
        />
        <ScoreCard
          title="Risk Profile"
          label={risk?.label}
          color={risk?.color}
          score={risk?.score}
          description={risk?.description}
          subtitle={`${risk?.total_critical_anomalies ?? 0} critical anomaly(ies)`}
        />
        <ScoreCard
          title="Target Performance"
          label={tp?.status === "target_set" ? tp.pathway_label : "No Target"}
          color={tp?.status === "target_set" ? tp.pathway_color : "slate"}
          score={undefined}
          description={
            tp?.status === "target_set"
              ? `${tp.pct_of_target_used?.toFixed(1)}% of annual target used (${tp.months_complete}/12 months)`
              : "Set annual target to enable pathway tracking."
          }
          subtitle={tp?.status === "target_set" ? `Required: ${tp.required_monthly?.toFixed(2)} tCO2e/month` : undefined}
        />
      </div>

      {/* Key Insights */}
      {key_insights?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Key Insights</p>
          {key_insights.map((ins, i) => <InsightRow key={i} insight={ins} />)}
        </div>
      )}

      {/* Risk Factors */}
      {risk?.factors?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Risk Factors & Implications</p>
          {risk.factors.map((f, i) => (
            <div key={i} className="mb-3 last:mb-0">
              <div className="flex items-start gap-2">
                <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded mt-0.5 shrink-0 ${
                  f.severity === "high"   ? "bg-red-50 text-red-600"    :
                  f.severity === "medium" ? "bg-amber-50 text-amber-600": "bg-slate-50 text-slate-400"
                }`}>{f.severity?.toUpperCase()}</span>
                {/* V3: type badge */}
                {f.type && (
                  <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 mt-0.5 shrink-0 capitalize">
                    {f.type}
                  </span>
                )}
                <div>
                  <p className="text-[9px] font-bold text-slate-700">{f.factor}</p>
                  <p className="text-[8px] text-slate-500 mt-0.5">{f.detail}</p>
                  {/* V3: implication */}
                  {f.implication && (
                    <p className="text-[7px] text-slate-400 italic mt-1 border-l-2 border-slate-200 pl-2">{f.implication}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline Table */}
      {timeline?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Period Timeline</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-50">
                {["Period","tCO2e","Confidence","Decision","Anomalies","Locked"].map(h => (
                  <th key={h} className="text-left text-[9px] text-slate-400 px-3 py-2 font-semibold">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {timeline.slice().reverse().map(p => (
                  <tr key={p.period_label} className="border-b border-slate-50 hover:bg-slate-50/40">
                    <td className="px-3 py-2 font-bold text-[#0F172A] text-[9px]">{p.period_label}</td>
                    <td className="px-3 py-2 font-bold text-teal-700 text-[9px]">{(p.total_tco2e||0).toFixed(3)}</td>
                    <td className="px-3 py-2 text-[9px]">
                      {p.confidence_score != null ? (
                        <span className={`font-bold ${p.confidence_score >= 80 ? "text-emerald-600" : p.confidence_score >= 60 ? "text-teal-600" : "text-amber-600"}`}>
                          {p.confidence_score}/100
                        </span>
                      ) : <span className="text-slate-300 text-[8px]">legacy</span>}
                    </td>
                    <td className="px-3 py-2 text-[8px] text-slate-500">{p.decision_outcome || "—"}</td>
                    <td className="px-3 py-2">
                      {p.anomaly_count > 0 ? (
                        <span className={`text-[8px] font-bold ${p.critical_anomalies > 0 ? "text-red-600" : "text-amber-600"}`}>
                          {p.anomaly_count} {p.critical_anomalies > 0 ? "(!)" : ""}
                        </span>
                      ) : <span className="text-emerald-400 text-[8px]">✓</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.is_locked
                        ? <span className="text-[8px] font-bold text-emerald-600">Locked</span>
                        : <span className="text-[8px] text-slate-400">Draft</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Next Steps */}
      {pd?.next_steps?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Recommended Next Steps</p>
          {pd.next_steps.map((s, i) => (
            <div key={i} className="flex items-start gap-2 mb-2">
              <span className="text-[9px] font-bold text-teal-500 mt-0.5">{i+1}.</span>
              <p className="text-[9px] text-slate-600">{s}</p>
            </div>
          ))}
        </div>
      )}

      {/* V2: Forward Projection */}
      {data.forward_projection?.projection_available && (
        <div className={`rounded-xl border p-5 ${
          data.forward_projection.forecast_color === "emerald" ? "border-emerald-200 bg-emerald-50" :
          data.forward_projection.forecast_color === "amber"   ? "border-amber-200 bg-amber-50"   :
          "border-red-200 bg-red-50"
        }`} data-testid="forward-projection">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-3">
            Forward Projection — Year-End Forecast
          </p>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className={`text-sm font-bold mb-1 ${
                data.forward_projection.forecast_color === "emerald" ? "text-emerald-700" :
                data.forward_projection.forecast_color === "amber"   ? "text-amber-700"   : "text-red-700"
              }`}>{data.forward_projection.forecast_label}</p>
              <p className="text-[8px] text-slate-500">{data.forward_projection.projection_method}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-slate-700">
                {data.forward_projection.projected_year_end_tco2e?.toFixed(2)}
              </p>
              <p className="text-[8px] text-slate-400">projected tCO2e</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[8px]">
            <div className="bg-white/60 rounded-xl px-3 py-2">
              <p className="text-slate-400">Months complete</p>
              <p className="font-bold text-slate-700">{data.forward_projection.months_complete}/12</p>
            </div>
            <div className="bg-white/60 rounded-xl px-3 py-2">
              <p className="text-slate-400">Monthly rate</p>
              <p className="font-bold text-slate-700">{data.forward_projection.weighted_monthly_rate?.toFixed(3)} tCO2e</p>
            </div>
            {data.forward_projection.annual_target_tco2e && (
              <div className="bg-white/60 rounded-xl px-3 py-2">
                <p className="text-slate-400">Annual target</p>
                <p className="font-bold text-slate-700">{data.forward_projection.annual_target_tco2e?.toFixed(2)} tCO2e</p>
              </div>
            )}
            {data.forward_projection.gap_to_target != null && (
              <div className="bg-white/60 rounded-xl px-3 py-2">
                <p className="text-slate-400">Gap to target</p>
                <p className={`font-bold ${data.forward_projection.gap_to_target > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {data.forward_projection.gap_to_target > 0 ? "+" : ""}{data.forward_projection.gap_to_target?.toFixed(2)} tCO2e
                </p>
              </div>
            )}
          </div>
          {data.forward_projection.monthly_reduction_needed > 0 && (
            <p className="text-[8px] text-amber-700 mt-2 font-semibold">
              Monthly reduction needed: {data.forward_projection.monthly_reduction_needed?.toFixed(3)} tCO2e/month to meet target
            </p>
          )}
          {/* V3: Confidence Band */}
          {data.forward_projection.confidence_band && (
            <div className="mt-3 pt-3 border-t border-white/40">
              <p className="text-[8px] font-bold text-slate-500 mb-1.5">Confidence Band</p>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[8px] text-slate-500">Low:</span>
                <span className="text-[9px] font-bold text-slate-700">{data.forward_projection.confidence_band.lower_bound?.toFixed(2)} tCO2e</span>
                <span className="text-[8px] text-slate-400 mx-1">—</span>
                <span className="text-[8px] text-slate-500">High:</span>
                <span className="text-[9px] font-bold text-slate-700">{data.forward_projection.confidence_band.upper_bound?.toFixed(2)} tCO2e</span>
                <span className="text-[7px] text-slate-400 ml-1">(±{(data.forward_projection.confidence_band.total_uncertainty * 100).toFixed(0)}%)</span>
              </div>
              <p className="text-[7px] text-slate-400 italic">{data.forward_projection.confidence_band.band_label}</p>
            </div>
          )}
        </div>
      )}

      {/* V2: Consistency Index detail */}
      {data.trajectory?.consistency_index != null && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Trajectory Consistency Index
            </p>
            <span className={`text-sm font-bold ${
              data.trajectory.consistency_index >= 70 ? "text-emerald-600" :
              data.trajectory.consistency_index >= 45 ? "text-amber-600" : "text-red-600"
            }`}>{data.trajectory.consistency_index}/100</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
            <div className={`h-full rounded-full ${
              data.trajectory.consistency_index >= 70 ? "bg-emerald-400" :
              data.trajectory.consistency_index >= 45 ? "bg-amber-400" : "bg-red-400"
            }`} style={{ width: `${data.trajectory.consistency_index}%` }} />
          </div>
          {/* V3: narrative */}
          {data.trajectory.consistency_narrative && (
            <p className="text-[8px] text-slate-500 mb-3 italic">{data.trajectory.consistency_narrative}</p>
          )}
          {/* V3: per-component explanations */}
          {data.trajectory.consistency_breakdown?.components?.map((comp, i) => (
            <div key={i} className={`rounded-xl px-3 py-2 mb-1.5 border ${
              comp.severity === "significant" ? "bg-red-50 border-red-100" :
              comp.severity === "moderate"    ? "bg-amber-50 border-amber-100" :
              comp.severity === "minor"       ? "bg-yellow-50 border-yellow-100" :
              "bg-emerald-50 border-emerald-100"
            }`}>
              <div className="flex items-center justify-between mb-0.5">
                <p className="text-[8px] font-bold text-slate-700">{comp.label}</p>
                <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${
                  comp.deduction === 0 ? "bg-emerald-100 text-emerald-700" :
                  comp.severity === "significant" ? "bg-red-100 text-red-700" :
                  "bg-amber-100 text-amber-700"
                }`}>
                  {comp.deduction > 0 ? `-${comp.deduction}` : "0"} pts
                </span>
              </div>
              <p className="text-[7px] text-slate-500">{comp.verdict}</p>
              <p className="text-[7px] text-slate-400 italic mt-0.5">{comp.detail}</p>
            </div>
          ))}
        </div>
      )}

      {/* V3: CPC Split — Availability vs Verified */}
      {data.credibility?.critical_param_coverage && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Critical Parameter Coverage
          </p>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-[8px] text-slate-400 mb-1">Availability Coverage</p>
              <p className={`text-lg font-bold ${
                (data.credibility.critical_param_coverage.availability_coverage_pct || 0) >= 60
                  ? "text-teal-600" : "text-amber-600"
              }`}>{data.credibility.critical_param_coverage.availability_coverage_pct?.toFixed(0)}%</p>
              <p className="text-[7px] text-slate-400 mt-1">Data entered</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center border-2 border-dashed border-teal-200">
              <p className="text-[8px] text-slate-400 mb-1">Verified Coverage</p>
              <p className={`text-lg font-bold ${
                (data.credibility.critical_param_coverage.verified_coverage_pct || 0) >= 60
                  ? "text-emerald-600" : "text-red-600"
              }`}>{data.credibility.critical_param_coverage.verified_coverage_pct?.toFixed(0)}%</p>
              <p className="text-[7px] text-teal-600 font-semibold mt-1">Drives credibility</p>
            </div>
          </div>
          {data.credibility.critical_param_coverage.verdict && (
            <p className="text-[8px] text-slate-500 italic">{data.credibility.critical_param_coverage.verdict}</p>
          )}
          {data.credibility.critical_param_coverage.missing_params?.length > 0 && (
            <div className="mt-2">
              <p className="text-[8px] font-bold text-red-600 mb-1">Missing critical params:</p>
              <p className="text-[7px] text-red-500">{data.credibility.critical_param_coverage.missing_params.join(", ")}</p>
            </div>
          )}
        </div>
      )}

      {/* V3: Decision Confidence */}
      {pd?.decision_confidence && (
        <div className={`rounded-xl border p-5 ${
          pd.decision_confidence.level === "high"     ? "border-emerald-200 bg-emerald-50" :
          pd.decision_confidence.level === "moderate" ? "border-amber-200 bg-amber-50"    :
          "border-red-200 bg-red-50"
        }`} data-testid="decision-confidence">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Decision Confidence</p>
              <p className={`text-sm font-bold ${
                pd.decision_confidence.level === "high"     ? "text-emerald-700" :
                pd.decision_confidence.level === "moderate" ? "text-amber-700"   : "text-red-700"
              }`}>{pd.decision_confidence.label}</p>
            </div>
            <span className={`text-[8px] font-bold px-2 py-1 rounded-full ${
              pd.decision_confidence.level === "high"     ? "bg-emerald-100 text-emerald-700" :
              pd.decision_confidence.level === "moderate" ? "bg-amber-100 text-amber-700"     :
              "bg-red-100 text-red-700"
            }`}>
              {pd.decision_confidence.level?.toUpperCase()}
            </span>
          </div>
          <p className="text-[8px] text-slate-600 mt-2 leading-relaxed">{pd.decision_confidence.explanation}</p>
          {pd.decision_confidence.factors?.length > 0 && (
            <div className="mt-2 space-y-1">
              {pd.decision_confidence.factors.map((f, i) => (
                <p key={i} className="text-[7px] text-slate-500">• {f}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Risk Override Gates */}
      {pd?.risk_overrides_applied?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-[9px] font-bold text-amber-700 mb-2">Risk Override Gates Applied</p>
          {pd.risk_overrides_applied.map((o, i) => (
            <p key={i} className="text-[8px] text-amber-600">→ {o}</p>
          ))}
        </div>
      )}

      {/* Investment Narrative */}
      {narrative && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="investment-narrative">
          <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50"
            onClick={() => setShowNarrative(v => !v)}>
            <div className="flex items-center gap-2">
              <FileText size={13} className="text-teal-600" />
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Board Investment Narrative
              </p>
            </div>
            <span className="text-[8px] text-slate-400">{showNarrative ? "Hide" : "Show"}</span>
          </button>
          {showNarrative && (
            <div className="px-5 pb-5 space-y-3">
              {/* Headline */}
              <div className={`rounded-xl p-3 border ${
                narrative.outcome_key?.includes("strong") || narrative.outcome_key?.includes("finance_ready")
                  ? "bg-emerald-50 border-emerald-200" :
                narrative.outcome_key?.includes("risk") || narrative.outcome_key?.includes("not_fin")
                  ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"
              }`}>
                <p className="text-[9px] font-bold text-slate-700">{narrative.executive_headline}</p>
              </div>
              {/* Board summary */}
              <div>
                <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider mb-1">Board Summary</p>
                <p className="text-[9px] text-slate-600 leading-relaxed">{narrative.board_summary}</p>
              </div>
              {/* Key risks */}
              {narrative.key_risks?.length > 0 && (
                <div>
                  <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider mb-1">Key Risks</p>
                  {narrative.key_risks.slice(0,3).map((r, i) => (
                    <p key={i} className="text-[8px] text-red-600 mb-0.5">• {r}</p>
                  ))}
                </div>
              )}
              {/* Key opportunities */}
              {narrative.key_opportunities?.length > 0 && (
                <div>
                  <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider mb-1">Key Opportunities</p>
                  {narrative.key_opportunities.slice(0,3).map((o, i) => (
                    <p key={i} className="text-[8px] text-emerald-600 mb-0.5">• {o}</p>
                  ))}
                </div>
              )}
              {/* Next actions */}
              {narrative.next_actions?.length > 0 && (
                <div>
                  <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider mb-1">Next Actions</p>
                  {narrative.next_actions.slice(0,3).map((a, i) => (
                    <p key={i} className="text-[8px] text-teal-600 mb-0.5">{i+1}. {a}</p>
                  ))}
                </div>
              )}
              {/* Financial opportunity */}
              <div className="bg-slate-50 rounded-xl px-3 py-2">
                <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider mb-1">Financial Opportunity</p>
                <p className="text-[8px] text-slate-600">{narrative.financial_opportunity}</p>
              </div>
              <p className="text-[7px] text-slate-400 italic">{narrative.methodology} · {narrative.caveats?.[0]}</p>
            </div>
          )}
        </div>
      )}

      <p className="text-[8px] text-slate-400 italic text-center">
        {data.methodology} ·
        Platform-generated — not third-party validated
      </p>
    </div>
  );
}
